import { useState } from 'react';

import { isClient } from 'utils/helpers';

import { useInterval } from './useInterval';

/**
 * Use a global value that may not be available on initial load, like a
 * function from a third-party script.
 *
 * @example
 *
 * // window.myFunc is added by a third-party script with server issues that
 * // causes load times of 5+ seconds. The button will be disabled until the
 * // script has finished loading and the function is available.
 * function Component() {
 *   const myFunc = useLazyGlobalValue('myFunc');
 *   return <button disabled={!myFunc}>Run func</button>;
 * }
 */
export function useLazyGlobalValue<K extends keyof Window>(
	key: K,
	{
		isActive = true,
		pollingInterval = 1000,
	}: { isActive?: boolean; pollingInterval?: number } = {},
): Window[K] | undefined {
	const [hasValue, setHasValue] = useState(
		isClient() && window[key] !== undefined,
	);

	useInterval(
		() => {
			if (window[key] !== undefined) {
				setHasValue(true);
			}
		},
		isActive && !hasValue ? pollingInterval : null,
	);

	// Editor shows correct type info but linter apparently can't figure it out.
	// oxlint-disable-next-line typescript/no-unsafe-return
	return hasValue ? window[key] : undefined;
}
